import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { validateSession } from '../utils/functions';
import { printComanda } from '../utils/printComanda';
import { getAgenda, getSchedule, businessNow } from '../utils/reservationsApi';
import { includedOf, formatCounter, previewOrder, splitOrder, hasRoomLeft, menuByCodeOf } from '../utils/eventIncluded';

var config = process.env;

// El orden es el del mostrador, no alfabético: define el orden de los bloques en el POS.
const CATEGORY_ORDER = ['cold_drink', 'hot_drink', 'bakery', 'pizzeria', 'snack', 'other'];
// Primer nivel del POS: con las 6 categorías juntas seguía siendo demasiada cosa en pantalla.
// El cajero elige el rubro y sólo ve esas categorías. Los snacks van con Comidas: para el que
// atiende son picoteo, no "varios" — en Otros quedaría lo que no se consume en el local.
const CATEGORY_GROUPS = [
    { key: 'drinks', label: 'Bebidas', icon: 'local_cafe', cats: ['cold_drink', 'hot_drink'] },
    { key: 'food', label: 'Comidas', icon: 'restaurant', cats: ['bakery', 'pizzeria', 'snack'] },
    { key: 'other', label: 'Otros', icon: 'local_dining', cats: ['other'] },
];
// Se recuerda el rubro entre recargas: si el navegador del mostrador se refresca a mitad de
// un pedido, volver siempre a "Bebidas" obliga a re-navegar con gente esperando.
const GROUP_STORAGE_KEY = 'cafePosGroup';
const CATEGORY_LABELS = {
    cold_drink: 'Bebidas frías',
    hot_drink: 'Bebidas calientes',
    bakery: 'Panadería',
    pizzeria: 'Pizzería',
    snack: 'Snacks',
    other: 'Otros',
};
// Color + icono por categoría para las cards del POS (puro cosmético). Los íconos son de
// Material Icons v85 (el clásico, el que carga public/css/icon_font.css): NO usar nombres de
// Material Symbols como `bakery_dining` — en esa fuente no existen y el navegador termina
// dibujando el texto crudo del ligature.
const CATEGORY_STYLE = {
    cold_drink: { color: '#2d9cdb', icon: 'local_bar' },
    hot_drink: { color: '#eb5757', icon: 'local_cafe' },
    bakery: { color: '#f2c94c', icon: 'cake' },
    pizzeria: { color: '#e67e22', icon: 'local_pizza' },
    snack: { color: '#f2994a', icon: 'fastfood' },
    other: { color: '#27ae60', icon: 'local_dining' },
};
const PAYMENT_METHODS = [
    { id: 1, label: 'Efectivo' },
    { id: 5, label: 'POS' },
    { id: 2, label: 'Transferencia' },
    { id: 3, label: 'MercadoPago' },
];

function money(value) {
    const n = Number(value || 0);
    return `$ ${n.toLocaleString('es-UY')}`;
}

class CafeteriaPosPage extends Component {
    constructor() {
        super();
        this.state = {
            checked: false, role: 0, loading: true,
            menu: [],
            // Rubro visible (primer nivel). Se lee del storage con fallback al primero, para
            // que un valor viejo o basura no deje la grilla vacía sin explicación.
            group: (() => {
                const saved = localStorage.getItem(GROUP_STORAGE_KEY);
                return CATEGORY_GROUPS.some((g) => g.key === saved) ? saved : CATEGORY_GROUPS[0].key;
            })(),
            cart: {},
            seatedTables: [],          // mesas con sesión abierta (para atar el consumo)
            selectedReservationId: '', // destino: '' = Mostrador; si no, la mesa (reserva) elegida
            // 'now' = se cobra ya | 'table' = va a la cuenta de la mesa | 'account' = a la cuenta del
            // empleado | 'included' = mesa de un evento que declara qué incluye: lo que entra en lo
            // incluido sale sin cargo y lo demás va a la cuenta de la mesa.
            chargeMode: 'now',
            customerInput: '',         // cédula O nombre: un solo campo (ver el bloque Cliente)
            paymentType: 5,
        posDevices: [],
        posDeviceId: '', // POS: es el medio más usado en el mostrador
            disabled: false,
            lastKitchenOrderId: null,
            reprinting: false,
            customerFirstName: '',
            customerLastName: '',
            customerFound: false,
            employeeSale: false,     // ENI-111: modo "venta empleado" (precio de empleado + cédula obligatoria)
            employeeAccounts: [],    // saldos del equipo (spec-cuenta-empleados): se cargan al activar el modo
            employeeAccount: null,   // el saldo de la persona elegida, o null si todavía no se identificó
            discountCode: '',
            discountApplied: null,   // ENI-99: { code, originalPrice, discountAmount, totalPrice }
            discountLoading: false,
            // Saldo a favor (crédito en tienda): NO es un campo aparte. Sale del cliente del
            // pedido (si tipeaste su cédula) y se elige como un medio de pago más.
            creditBalance: 0,        // saldo del cliente identificado; 0 = no hay nada que ofrecer
            payWithCredit: false,    // el medio elegido es "Saldo a favor"
            // Voucher de premio o cortesía (backend docs/spec-premios-cortesias-vouchers.md
            // FR-104): Cobro = 'voucher'. El código se valida contra el pedido antes de confirmar.
            voucherCode: '',
            voucherPreview: null,    // { label, benefit, fits, reason } del último "Validar"
            voucherLoading: false,
        };
    }

    // Guard de doble click: la térmica imprime directo (sin el diálogo del navegador
    // que antes serializaba), así que dos clicks rápidos = dos tickets físicos.
    reprintLast = () => {
        if (this.state.reprinting || !this.state.lastKitchenOrderId) {
            return;
        }
        this.setState({ reprinting: true });
        Promise.resolve(printComanda(this.state.lastKitchenOrderId))
            .finally(() => this.setState({ reprinting: false }));
    }

    async componentDidMount() {
        this.loadPosDevices();
        const role = await validateSession();
        this.setState({ checked: true, role });
        this.fetchMenu();
        this.fetchSeatedTables();
    }

    // Mesas con sesión abierta (Fase 2): el destino "Mesa" se elige de esta lista en vez de
    // tipear el número a mano, y así el consumo queda atado a la mesa (purchase.reservation_id).
    fetchSeatedTables = async () => {
        try {
            // Jornada del local (puede cruzar medianoche): a las 00:30 con cierre 01:00 las
            // mesas sentadas siguen siendo las de "ayer".
            let schedule = null;
            try { const s = await getSchedule(); schedule = s && s.schedule; } catch (e) { /* defaults */ }
            const today = businessNow(schedule).date;
            const agenda = await getAgenda(today, today);
            const tables = (agenda && agenda.tables) || [];
            const seated = ((agenda && agenda.reservations) || [])
                .filter((r) => r.status === 'seated')
                .map((r) => {
                    const table = tables.find((t) => t.id === r.table_id);
                    return {
                        reservationId: r.id,
                        label: (r.table && r.table.label) || (table && table.label) || `Mesa ${r.table_id}`,
                        playerCount: r.player_count,
                        name: r.name || null,
                        // El adjunto de actividad, tal cual: si la mesa es de un evento que declara
                        // qué incluye, trae el resumen (lo pactado contra lo entregado).
                        activity: r.activity || null,
                    };
                });
            // Si venimos del salón con una mesa elegida ("Tomar pedido en el POS"), dejarla
            // seleccionada. Se consume una sola vez para no pegarse en pedidos siguientes.
            const preselect = localStorage.getItem('posPreselectReservation');
            localStorage.removeItem('posPreselectReservation');
            const valid = preselect && seated.some((t) => String(t.reservationId) === String(preselect));
            // Esto también corre como refresco (al enfocar Destino, o cuando el backend rechaza un
            // pedido incluido): la mesa que estaba elegida puede haberse liberado mientras tanto.
            const selectedId = valid ? preselect : this.state.selectedReservationId;
            const selected = seated.find((t) => String(t.reservationId) === String(selectedId));
            const gone = selectedId !== '' && !selected;
            this.setState({
                seatedTables: seated,
                ...(valid ? { selectedReservationId: preselect } : {}),
                ...(gone ? { selectedReservationId: '' } : {}),
            });
            if (gone) {
                // El Destino ya no está sentado: Mostrador y "Cobrar ahora" (FR-075).
                this.setChargeMode('now');
            } else if (valid && includedOf(selected)) {
                // "Tomar pedido" sobre una mesa de un evento con inclusiones: su Cobro por defecto.
                this.setChargeMode(this.eventChargeMode(selected));
            } else if (selected && !includedOf(selected) && this.state.chargeMode === 'included') {
                // La mesa sigue sentada pero ya no trae resumen: deja de ser una mesa "de evento
                // con inclusiones" y ese modo no existe fuera de ellas.
                this.setChargeMode('now');
            }
        } catch (error) {
            // Sin agenda al abrir, la lista queda vacía (sólo Mostrador). Si lo que falló fue un
            // refresco, se conserva la que había: un corte de red no es "la mesa se liberó".
            this.setState({ seatedTables: this.state.seatedTables || [] });
        }
    }

    fetchMenu = () => {
        const request = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'cafeteria/catalog', request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    this.setState({ loading: false });
                } else {
                    this.setState({ menu: data.data || [], loading: false });
                }
            });
    }

    // Lo tipeado es una cédula si son sólo dígitos (los nombres no lo son) — en venta empleado
    // el campo SIEMPRE es la cédula. De esto depende si se resuelve la ficha y el saldo.
    customerIsCedula = () => (this.state.employeeSale || /^\d{4,}$/.test((this.state.customerInput || '').trim()));

    onChangeCustomer = (value) => {
        // Cambió el cliente: la ficha y el saldo resueltos ya no son suyos.
        this.setState({
            customerInput: value,
            customerFound: false,
            customerFirstName: '',
            customerLastName: '',
            creditBalance: 0,
            payWithCredit: false,
        });
    }

    // Busca el cliente por cédula (como el alta retail) y, de paso, su saldo a favor: es el
    // mismo dato del mismo cliente, así que pedirlo dos veces (o pedir la cédula de nuevo más
    // abajo) no tiene sentido. Si no existe, deja los campos editables para tipear el nombre:
    // el backend crea la ficha al cobrar.
    lookupCustomer = () => {
        const cedula = (this.state.customerInput || '').trim();
        if (cedula === '' || !this.customerIsCedula()) {
            // Un nombre no tiene ficha ni saldo que buscar.
            this.setState({ customerFound: false, creditBalance: 0, payWithCredit: false });
            return;
        }
        const request = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        };
        const api = config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
        fetch(api + 'user/' + encodeURIComponent(cedula), request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error === 0 && data.data) {
                    this.setState({
                        customerFound: true,
                        customerFirstName: data.data.firstName || '',
                        customerLastName: data.data.lastName || '',
                    });
                } else {
                    this.setState({ customerFound: false });
                }
            })
            .catch(() => this.setState({ customerFound: false }));
        // El saldo va aparte del alta: un cliente sin ficha simplemente no tiene saldo, y que
        // falle esta consulta no puede impedir cobrarle.
        fetch(api + 'store-credit/' + encodeURIComponent(cedula), request)
            .then((results) => results.json())
            .then((data) => {
                const balance = (data.error === 0 && data.data) ? Number(data.data.balance || 0) : 0;
                this.setState({ creditBalance: balance > 0 ? balance : 0, payWithCredit: false });
            })
            .catch(() => this.setState({ creditBalance: 0, payWithCredit: false }));
    }

    addToCart = (item) => {
        const cart = { ...this.state.cart };
        if (cart[item.code]) {
            cart[item.code] = { ...cart[item.code], quantity: cart[item.code].quantity + 1 };
        } else {
            // ENI-111: se guarda también employeePrice para poder re-preciar al togglear "venta empleado".
            cart[item.code] = {
                code: item.code, name: item.name, price: item.price,
                employeePrice: item.employeePrice != null ? item.employeePrice : null,
                quantity: 1, notes: '',
            };
        }
        // ENI-99: al cambiar el pedido, el descuento aplicado se invalida (hay que re-aplicarlo).
        this.setState({ cart, discountApplied: null });
    }

    changeQty = (code, delta) => {
        const cart = { ...this.state.cart };
        const line = cart[code];
        if (!line) return;
        const next = line.quantity + delta;
        if (next <= 0) {
            delete cart[code];
        } else {
            cart[code] = { ...line, quantity: next };
        }
        this.setState({ cart, discountApplied: null });
    }

    changeNote = (code, value) => {
        const cart = { ...this.state.cart };
        if (cart[code]) {
            cart[code] = { ...cart[code], notes: value };
            this.setState({ cart });
        }
    }

    // ENI-99: valida el código contra el pedido (preview) y muestra el total con descuento.
    applyDiscount = () => {
        const code = (this.state.discountCode || '').trim();
        if (code === '') { alert('Ingresá un código de descuento.'); return; }
        const lines = Object.keys(this.state.cart).map((k) => this.state.cart[k]);
        if (lines.length === 0) { alert('El pedido está vacío.'); return; }
        this.setState({ discountLoading: true });
        const request = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
            body: JSON.stringify({ items: lines.map((l) => ({ code: l.code, quantity: l.quantity })), code }),
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'cafeteria/discount/preview', request)
            .then((results) => results.json())
            .then((data) => {
                this.setState({ discountLoading: false });
                if (data.error > 0) { alert(data.message || 'No se pudo validar el código.'); return; }
                if (!data.data.valid) { this.setState({ discountApplied: null }); alert(data.data.message || 'Código inválido.'); return; }
                this.setState({ discountApplied: data.data });
            })
            .catch(() => { this.setState({ discountLoading: false }); alert('Error de red al validar el código.'); });
    }

    clearDiscount = () => this.setState({ discountApplied: null, discountCode: '' });

    // El código de descuento se aplica al COBRAR el pedido. En venta de empleado es excluyente
    // (D-04), y al cargar a la mesa todavía no se cobra nada: la cuenta se cobra después, a
    // precio de lista, y el backend rechaza la combinación (6012). Devuelve el motivo por el que
    // el campo está bloqueado, o null si se puede usar.
    discountBlockedReason = () => {
        if (this.state.employeeSale) return 'No disponible en venta empleado';
        if (this.state.chargeMode === 'table') return 'Sólo al cobrar ahora';
        if (this.state.chargeMode === 'included') return 'No aplica a lo incluido en el evento';
        if (this.state.chargeMode === 'voucher') return 'No se combina con un voucher';
        return null;
    }

    // Valida el voucher contra el pedido: qué cubre y si entra, sin consumir nada (FR-104).
    previewVoucher = () => {
        const code = (this.state.voucherCode || '').trim();
        if (code === '') { alert('Ingresá el código del voucher.'); return; }
        const lines = Object.keys(this.state.cart).map((k) => this.state.cart[k]);
        this.setState({ voucherLoading: true });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'cafeteria/voucher/preview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
            body: JSON.stringify({ code, items: lines.map((l) => ({ code: l.code, quantity: l.quantity })) }),
        })
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    this.setState({ voucherLoading: false, voucherPreview: null });
                    alert(data.message || 'No se pudo validar el voucher.');
                    return;
                }
                this.setState({ voucherLoading: false, voucherPreview: data.data });
            })
            .catch(() => { this.setState({ voucherLoading: false }); alert('Error de red al validar el voucher.'); });
    }

    // TODA escritura de `chargeMode` pasa por acá (salvo `setEmployeeSale` y el reset después de
    // la venta, que limpian el código por su cuenta). Al salir de "cobrar ahora" se saca el
    // código que estuviera aplicado: si quedara, el total seguiría mostrándose rebajado por un
    // descuento que esa comanda no va a llevar, y al volver a "cobrar ahora" reaparecería
    // aplicado sin que nadie lo viera entrar.
    setChargeMode = (mode) => this.setState((prev) => ({
        chargeMode: mode,
        discountApplied: mode === 'now' ? prev.discountApplied : null,
        discountCode: mode === 'now' ? prev.discountCode : '',
        // El voucher sólo vive en su modo: al salir se olvida, para no mandarlo sin querer.
        voucherCode: mode === 'voucher' ? prev.voucherCode : '',
        voucherPreview: mode === 'voucher' ? prev.voucherPreview : null,
        // Con voucher no hay saldo a favor (el backend rechaza la combinación: 3318).
        payWithCredit: mode === 'voucher' ? false : prev.payWithCredit,
    }));

    // El Cobro por defecto de una mesa de un evento que declara qué incluye: "Incluido" mientras a
    // algún renglón le falte entregar —van a consumir primero lo incluido— y "Cargar a la mesa"
    // cuando ya no queda nada. Es sólo el valor con el que arranca: se puede cambiar.
    eventChargeMode = (table) => (hasRoomLeft(includedOf(table)) ? 'included' : 'table');

    // Cambio de Destino. Una mesa de un evento que declara qué incluye arranca en su Cobro por
    // defecto (nunca en el modo heredado del pedido anterior). Mostrador vuelve a "cobrar ahora";
    // una mesa común conserva el modo que había, salvo que fuera "Incluido", que sólo existe en
    // mesas de evento (FR-073). Con la venta de empleado activa "Incluido" no se ofrece, así que
    // la mesa del evento se trata como una común.
    onChangeDestination = (reservationId) => {
        const previous = this.state.chargeMode;
        const table = (this.state.seatedTables || []).find((t) => String(t.reservationId) === String(reservationId));
        this.setState({ selectedReservationId: reservationId });
        if (table && includedOf(table) && !this.state.employeeSale) {
            this.setChargeMode(this.eventChargeMode(table));
        } else if (!reservationId || previous === 'included') {
            this.setChargeMode('now');
        } else {
            this.setChargeMode(previous);
        }
    }

    // Por qué no se puede elegir "Incluido en el evento", o null si se puede (FR-060, FR-124).
    // Una mesa de evento que no trae resumen —el evento no declara nada, o el backend no lo pudo
    // calcular— se trata igual: quien decide de verdad es el server (FR-122).
    includedBlockedReason = () => {
        const seated = this.selectedSeated();
        if (seated && includedOf(seated)) {
            return this.state.employeeSale ? 'No se combina con la venta de empleado' : null;
        }
        if (seated && seated.activity && seated.activity.eventId != null) return 'El evento no declara qué incluye';
        return 'Sólo para mesas de un evento';
    }

    // ENI-111: precio efectivo de una línea. En modo empleado usa employeePrice si el ítem lo
    // tiene; si no, el precio normal (D-06). El backend recomputa el total autoritativo.
    linePrice = (l) => ((this.state.employeeSale && l.employeePrice != null) ? Number(l.employeePrice) : Number(l.price));

    // ENI-111: al activar/desactivar el modo empleado se re-precia el carrito. Es EXCLUYENTE con
    // el código de descuento (D-04): activarlo limpia el descuento aplicado.
    setEmployeeSale = (on) => {
        this.setState((prev) => ({
            employeeSale: on,
            discountApplied: on ? null : prev.discountApplied,
            discountCode: on ? '' : prev.discountCode,
            // FR-011: en una venta de empleado la forma de cobro que viene elegida es "a cuenta".
            // Es el default porque es lo que va a pasar casi siempre; cobrar en el momento sigue
            // estando a un click. Al apagar el modo se vuelve a "cobrar ahora": el medio 6 no
            // existe fuera de la venta de empleado y el backend lo rechaza con 3600. Salvo que el
            // Destino sea una mesa de un evento con inclusiones: ahí vuelve a su Cobro por defecto.
            chargeMode: on ? 'account' : (prev.chargeMode === 'account'
                ? ((table) => (includedOf(table) ? this.eventChargeMode(table) : 'now'))(
                    (prev.seatedTables || []).find((t) => String(t.reservationId) === String(prev.selectedReservationId)))
                : prev.chargeMode),
            employeeAccount: on ? prev.employeeAccount : null,
            // El saldo a favor es del cliente identificado: al cambiar de modo hay que volver a
            // resolverlo (la cédula del empleado no es la del cliente del pedido).
            creditBalance: 0,
            payWithCredit: false,
        }), () => {
            if (on) {
                this.fetchEmployeeAccounts();
            }
        });
    }

    // Saldos del equipo. Se piden al activar el modo empleado y no en cada tecla de la cédula: son
    // pocas personas y el listado ya trae el aviso de tope RESUELTO por el backend, así que el POS
    // no compara contra ningún número hardcodeado (el tope se cambia sin deploy, FR-006).
    fetchEmployeeAccounts = () => {
        const request = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'employeeAccount', request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error === 0 && data.data) {
                    this.setState({ employeeAccounts: data.data.accounts || [] }, this.matchEmployeeAccount);
                }
            })
            .catch(() => { /* el saldo es informativo: si no se puede leer, la venta sigue */ });
    }

    // Busca en los saldos ya cargados el de la cédula tipeada. Sin match, no se muestra nada:
    // mostrar "saldo $0" para una cédula que todavía no se terminó de tipear sería mentir.
    matchEmployeeAccount = () => {
        const cedula = (this.state.customerInput || '').trim();
        const found = cedula === ''
            ? null
            : (this.state.employeeAccounts || []).find((a) => String(a.cedula) === cedula);
        this.setState({ employeeAccount: found || null });
    }

    // Total a cobrar por el pedido (con el cupón aplicado si hay).
    orderTotal = () => (this.state.discountApplied ? Number(this.state.discountApplied.totalPrice) : this.total());

    // Saldo que se va a usar. Siempre es TODO lo que alcance: el saldo es del cliente y no hay
    // motivo para dejarle una parte sin usar. Si no llega a cubrir el pedido, la diferencia se
    // cobra con el medio elegido (this.state.paymentType). Cargar el consumo —a la mesa o a la
    // cuenta del empleado— no cobra nada, así que ahí no hay saldo que gastar: el backend
    // rechaza las dos combinaciones (6011 la mesa, 3612 la cuenta del equipo).
    appliedCredit = () => ((this.state.payWithCredit && this.state.chargeMode === 'now')
        ? Math.min(Number(this.state.creditBalance) || 0, this.orderTotal())
        : 0);

    // Lo que hay que cobrar por el medio de pago, ya descontado el saldo.
    amountToCharge = () => Math.max(0, this.orderTotal() - this.appliedCredit());

    // ¿El saldo alcanza para todo el pedido? Decide si hay que pedir un segundo medio de pago.
    creditCoversAll = () => this.state.payWithCredit && this.amountToCharge() === 0;

    // El combo de cobro maneja los medios de pago Y el saldo, porque para el cajero el saldo ES
    // una forma de pagar. El saldo sólo aparece si el cliente identificado tiene: si no, el
    // combo queda exactamente como antes.
    onChangePayment = (value) => {
        if (value === 'credit') { this.setState({ payWithCredit: true }); return; }
        this.setState({ payWithCredit: false, paymentType: Number(value) });
    }

    total = () => Object.keys(this.state.cart).reduce((sum, k) => {
        const l = this.state.cart[k];
        return sum + this.linePrice(l) * l.quantity;
    }, 0);

    // Mesa elegida de la lista viva (sesión abierta). '' = ninguna / no hay mesas sentadas.
    selectedSeated = () => (this.state.seatedTables || []).find(
        (t) => String(t.reservationId) === String(this.state.selectedReservationId)
    );

    // Un solo combo de destino: Mostrador o una de las mesas con gente sentada.
    destination = () => {
        const seated = this.selectedSeated();
        return seated ? seated.label : 'Mostrador';
    };

    // El pedido del carrito repartido contra lo incluido del evento, o null si no corresponde: sólo
    // existe en una mesa de un evento con inclusiones y con Cobro en "Incluido".
    cartSplit = () => {
        const summary = includedOf(this.selectedSeated());
        if (this.state.chargeMode !== 'included' || !summary) return null;
        const lines = Object.keys(this.state.cart).map((k) => this.state.cart[k]);
        return splitOrder(summary, lines, menuByCodeOf(this.state.menu));
    }

    // El cuerpo de `POST cafeteria/sale` para estas líneas y esta forma de cobro.
    saleBody = (lines, mode, seatedTable) => {
        const included = mode === 'included';
        // Voucher: como lo incluido, sin medio de pago (lo pone el backend: PREMIO).
        const conVoucher = mode === 'voucher';
        const body = {
            items: lines.map((l) => ({ code: l.code, quantity: l.quantity, notes: l.notes || undefined })),
            // Lo incluido no lleva medio de pago: el ticket lo pone el backend. Y un backend que
            // todavía no conoce el modo rechaza el pedido (1001) en vez de cobrarlo (NFR-010).
            ...(included ? { includedInEvent: true }
                : conVoucher ? { voucherCode: (this.state.voucherCode || '').trim() }
                    : { paymentType: this.state.paymentType }),
            destination: this.destination(),
        };
        // Sesión de mesa (Fase 2): atar el consumo a la mesa sentada para poder ver
        // cuánto lleva consumido cada mesa (el `destination` es sólo texto para la cocina).
        if (seatedTable) {
            body.reservationId = seatedTable.reservationId;
        }
        // "Cargar a la mesa": la comanda se prepara ahora y queda impaga en la cuenta.
        if (mode === 'table' && seatedTable) {
            body.deferPayment = true;
        } else if (mode === 'account') {
            // "Cargar a la cuenta": la venta se salda contra la cuenta corriente del empleado. El
            // medio de pago lo pone el backend; mandar `paymentType` acá no cambiaría nada.
            body.chargeToAccount = true;
        } else if (!included && !conVoucher && this.state.paymentType === 5 && this.state.posDeviceId) {
            // F1 del arqueo: por cuál lector entró. Sólo en el cobro inmediato con tarjeta —
            // en el diferido el medio de pago se elige recién al liquidar la mesa, y lo incluido
            // en un evento no pasa por ningún lector.
            body.posDeviceId = Number(this.state.posDeviceId);
        }
        // Nombre para la comanda: lo tipeado si fue un nombre, o el del cliente resuelto por
        // cédula — la cocina necesita saber de quién es el pedido, no un número.
        const typed = (this.state.customerInput || '').trim();
        const resolvedName = `${this.state.customerFirstName || ''} ${this.state.customerLastName || ''}`.trim();
        const customerName = this.customerIsCedula() ? resolvedName : typed;
        if (customerName) {
            body.customerName = customerName;
        }
        // ENI-111: modo venta empleado (excluyente con el código de descuento, D-04).
        if (this.state.employeeSale) {
            body.employeeSale = true;
        } else if (!included && !conVoucher && this.state.discountApplied && this.state.discountApplied.code) {
            // ENI-99: si hay un descuento aplicado (validado con el preview), se manda el código.
            body.discountCode = this.state.discountApplied.code;
        }
        // Cliente opcional: si lo tipeado fue una cédula, la mandamos (el backend crea la ficha
        // si no existe); si fue un nombre, o está vacío, el backend usa el consumidor final.
        if (this.customerIsCedula() && typed !== '') {
            body.cedula = typed;
            body.firstName = (this.state.customerFirstName || '').trim();
            body.lastName = (this.state.customerLastName || '').trim();
        }
        // Saldo a favor: es del cliente del pedido, que ya viaja como `cedula` arriba (el saldo
        // sólo se ofrece cuando hay uno identificado con saldo). Si cubre el pedido entero no
        // queda nada que cobrar: el medio de pago del ticket pasa a ser EFECTIVO, el balde más
        // inocuo del arqueo — un ticket de $0 en transferencia le pediría al cajero un
        // comprobante que no existe.
        if (!included && !conVoucher && this.appliedCredit() > 0) {
            body.creditApplied = this.appliedCredit();
            if (this.creditCoversAll()) {
                body.paymentType = 1;
            }
        }
        return body;
    }

    postSale = (body) => fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'cafeteria/sale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        body: JSON.stringify(body),
    }).then((results) => results.json());

    // El resumen es del evento, no de la mesa: se actualiza en todas las suyas.
    applyEventSummary = (newSummary) => {
        if (!newSummary) return;
        this.setState((prev) => ({
            seatedTables: (prev.seatedTables || []).map((t) => {
                const current = includedOf(t);
                return current && String(current.eventId) === String(newSummary.eventId)
                    ? { ...t, activity: { ...t.activity, included: newSummary } }
                    : t;
            }),
        }));
    }

    // La venta no se registró: nada cobrado ni cargado, el carrito queda como estaba.
    saleRejected = (data, included) => {
        this.setState({ disabled: false });
        if (data.error === 7609) {
            // Otro POS cargó algo incluido mientras se armaba este pedido y ya no entra entero.
            // La respuesta trae el contador al día: con él se vuelve a repartir el carrito.
            const fresh = data.data && data.data.eventIncluded ? data.data.eventIncluded.summary : null;
            if (fresh != null && typeof fresh === 'object') {
                this.applyEventSummary(fresh);
            } else {
                this.fetchSeatedTables();
            }
            alert('Lo incluido del evento cambió mientras armabas el pedido. Revisá cómo quedó y confirmá de nuevo.');
            return;
        }
        // Un backend sin esta función no conoce `includedInEvent`: ve un pedido sin
        // medio de pago y lo rechaza con el texto genérico del 1001. No cobró nada.
        alert(included && data.error === 1001
            ? 'El servidor todavía no admite pedidos incluidos. No se registró nada: avisá, y mientras tanto cobralo o cargalo a la mesa.'
            : data.message);
        if (data.error === 1002) {
            window.location.href = localStorage.getItem('homePageURL');
        }
        // La mesa dejó de estar sentada, el evento se canceló o ya no declara qué
        // incluye: lo que hay en pantalla está viejo.
        if ([7600, 7601, 7602].includes(data.error)) {
            this.fetchSeatedTables();
        }
    }

    // La venta quedó registrada. `keepOrder` es el caso de un pedido que sale en dos ventas (lo
    // incluido y los extras) cuando todavía falta la segunda: se hace todo lo de una venta
    // registrada —contador, avisos, comanda— menos vaciar el pedido.
    saleRegistered = (data, keepOrder = false) => {
        const koId = data.data.kitchenOrderId;
        // El saldo resultante viene en la respuesta de la venta: se avisa acá y no en la
        // próxima venta, que es cuando ya sería tarde para decir algo.
        const cuenta = data.data.employeeAccount;
        // El contador del evento ya con este pedido. Viaja aparte de `warnings`, que sigue
        // siendo sólo el aviso de stock; null si el backend no lo pudo calcular.
        const eventIncluded = data.data.eventIncluded || null;
        const newSummary = eventIncluded && eventIncluded.summary != null && typeof eventIncluded.summary === 'object'
            ? eventIncluded.summary
            : null;
        this.applyEventSummary(newSummary);
        if (keepOrder) {
            this.setState({ lastKitchenOrderId: koId || this.state.lastKitchenOrderId });
        } else {
            this.setState((prev) => {
                // En una mesa de un evento con inclusiones vienen varios pedidos seguidos: se
                // conserva el Destino y Cobro vuelve a su valor por defecto, calculado con el
                // contador nuevo. En cualquier otro caso, Mostrador y "cobrar ahora", como
                // siempre (FR-074).
                //
                // Se decide con el Destino que hay AHORA, no con el del click: el combo sigue
                // activo con la venta en vuelo, y si se pasó a Mostrador o a una mesa común
                // Cobro no puede quedar en un modo de mesa de evento (FR-073).
                const destination = (prev.seatedTables || []).find((t) => String(t.reservationId) === String(prev.selectedReservationId));
                return {
                    cart: {}, customerInput: '',
                    ...(includedOf(destination) ? { chargeMode: this.eventChargeMode(destination) } : { selectedReservationId: '', chargeMode: 'now' }),
                    disabled: false, lastKitchenOrderId: koId,
                    customerFirstName: '', customerLastName: '', customerFound: false,
                    employeeSale: false, discountCode: '', discountApplied: null,
                    employeeAccount: null, employeeAccounts: [],
                    creditBalance: 0, payWithCredit: false,
                    voucherCode: '', voucherPreview: null,
                };
            });
        }
        // Voucher canjeado: que el cajero vea qué se entregó y que no se cobra nada.
        if (data.data.voucher) {
            alert(`Voucher canjeado: ${data.data.voucher.label || data.data.voucher.benefit}. Sin cobro.`);
        }
        // La venta quedó registrada pero el backend no pudo calcular el resumen: el contador
        // en pantalla no tiene este pedido. Se vuelven a pedir las mesas para ponerlo al día.
        if (eventIncluded && !newSummary) {
            this.fetchSeatedTables();
        }
        if (cuenta && cuenta.warn) {
            alert(`Cargado a la cuenta. Saldo: ${money(cuenta.balance)} — pasó el tope de ${money(cuenta.threshold)}, conviene que salde.`);
        }
        // ENI-90 (Q-05): avisos no bloqueantes de stock (se vendió por debajo de lo disponible).
        const warnings = data.data.warnings || [];
        if (warnings.length > 0) {
            alert('Venta registrada. Avisos de stock:\n' + warnings.map((w) => `· ${w.name || w.code}: disponible ${w.available}, pedido ${w.requested}`).join('\n'));
        }
        // Refrescar el catálogo para reflejar la disponibilidad ya descontada.
        this.fetchMenu();
        if (koId) {
            printComanda(koId);
        } else if (data.data.comandaError) {
            if (window.confirm('Venta registrada pero falló la comanda. ¿Regenerar e imprimir?')) {
                this.repairAndPrint(data.data.purchaseId);
            }
        }
    }

    confirm = () => {
        // Con un pedido en vuelo no sale otro (el botón ya está deshabilitado; esto cubre el
        // segundo disparo que llega antes de que la pantalla se repinte).
        if (this.state.disabled) return;
        const lines = Object.keys(this.state.cart).map((k) => this.state.cart[k]);
        if (lines.length === 0) {
            alert('El pedido está vacío.');
            return;
        }
        // ENI-111: en modo empleado la cédula es obligatoria (el backend valida que sea empleado).
        if (this.state.employeeSale && (this.state.customerInput || '').trim() === '') {
            alert('La venta de empleado requiere la cédula del empleado.');
            return;
        }
        if (this.state.chargeMode === 'voucher' && (this.state.voucherCode || '').trim() === '') {
            alert('Ingresá el código del voucher.');
            return;
        }
        const seatedTable = this.selectedSeated();
        // Eventos con consumo incluido. `summary` sólo existe si el Destino es una mesa de un
        // evento que declara qué incluye: una mesa común (o Mostrador) sigue de largo, sin
        // ningún diálogo, por el camino de siempre.
        const summary = includedOf(seatedTable);
        let mode = this.state.chargeMode;
        if (summary && mode === 'included') {
            // El pedido se reparte: lo que entra en lo incluido sale sin cargo y lo demás va a la
            // cuenta de la mesa. Es lo que ya se ve en pantalla, línea por línea.
            const split = this.cartSplit();
            if (split.included > 0) {
                this.confirmSplit(lines, split, seatedTable);
                return;
            }
            // Nada entra en lo incluido: es un pedido cargado a la mesa, y sale como tal.
            mode = 'table';
        } else if (summary && mode === 'table') {
            // Se eligió a mano cobrar algo que todavía entra en lo incluido: se pregunta. Si el
            // operador no acepta, no se toca nada.
            const preview = previewOrder(summary, lines, menuByCodeOf(this.state.menu));
            if (preview.unused.length > 0) {
                const sinUsar = preview.unused
                    .map((u) => `${u.label}: ${u.remaining === 1 ? 'queda 1 incluida' : `quedan ${u.remaining} incluidas`} sin usar`)
                    .join('. ');
                if (!window.confirm(`${sinUsar}. ¿Cargar igual a la cuenta de la mesa? Se cobra.`)) return;
            }
        }
        this.setState({ disabled: true });
        this.postSale(this.saleBody(lines, mode, seatedTable))
            .then((data) => {
                if (data.error > 0) {
                    this.saleRejected(data, mode === 'included');
                    return;
                }
                this.saleRegistered(data);
            })
            .catch(() => {
                this.setState({ disabled: false });
                alert('No se pudo registrar la venta. Reintentá.');
            });
    }

    // Un pedido incluido en una mesa del evento: hasta DOS ventas, en orden. Primero lo incluido
    // (sin cargo); si anduvo y hay extras, los extras cargados a la mesa. Cada venta que anda
    // imprime su comanda; la nota de una línea que se parte viaja en las dos.
    confirmSplit = (lines, split, seatedTable) => {
        const part = (key) => lines
            .filter((l) => split.byCode[l.code] && split.byCode[l.code][key] > 0)
            .map((l) => ({ ...l, quantity: split.byCode[l.code][key] }));
        const includedLines = part('included');
        const extraLines = part('extra');
        // Los dos cuerpos se arman con lo que hay al confirmar: el Destino sigue activo con la
        // venta en vuelo, y los extras no pueden salir con la mesa de uno y el cartel de otro.
        const extrasBody = this.saleBody(extraLines, 'table', seatedTable);
        this.setState({ disabled: true });
        this.postSale(this.saleBody(includedLines, 'included', seatedTable))
            .then((data) => {
                if (data.error > 0) {
                    // No se cargó nada: ni lo incluido ni los extras. El carrito queda entero.
                    this.saleRejected(data, true);
                    return null;
                }
                if (extraLines.length === 0) {
                    this.saleRegistered(data);
                    return null;
                }
                this.saleRegistered(data, true);
                // Lo incluido ya está registrado y su comanda impresa. Si los extras no se
                // cargan, en el pedido quedan sólo ellos, listos para confirmar de nuevo.
                const extrasFailed = (rejected) => {
                    this.setState({
                        disabled: false,
                        cart: extraLines.reduce((cart, l) => ({ ...cart, [l.code]: l }), {}),
                    });
                    this.setChargeMode('table');
                    alert('Lo incluido quedó registrado. Los extras no se cargaron: quedaron en el pedido, confirmalos de nuevo.');
                    if (rejected && rejected.error === 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                };
                return this.postSale(extrasBody).then((second) => {
                    if (second.error > 0) {
                        extrasFailed(second);
                        return;
                    }
                    this.saleRegistered(second);
                }, () => extrasFailed(null));
            })
            .catch(() => {
                this.setState({ disabled: false });
                alert('No se pudo registrar la venta. Reintentá.');
            });
    }

    repairAndPrint = (purchaseId) => {
        const request = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/sale/${purchaseId}/repair-order`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error === 0 && data.data.kitchenOrderId) {
                    this.setState({ lastKitchenOrderId: data.data.kitchenOrderId });
                    printComanda(data.data.kitchenOrderId);
                } else {
                    alert(data.message || 'No se pudo regenerar la comanda.');
                }
            });
    }

    selectGroup = (key) => {
        localStorage.setItem(GROUP_STORAGE_KEY, key);
        this.setState({ group: key });
    }

    loadPosDevices = () => {
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'posDevices', {
            headers: { 'access-token': localStorage.getItem('token') },
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data.error === 0 && Array.isArray(data.data) && data.data.length > 0) {
                    // Preseleccionado el de cafetería si existe: es el que está al lado de esta
                    // pantalla. Igual queda visible, porque a veces se cobra en el de la tienda.
                    const preferido = data.data.find((d) => /cafeter/i.test(d.name)) || data.data[0];
                    this.setState({ posDevices: data.data, posDeviceId: String(preferido.id) });
                }
            })
            .catch(() => {});
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const grouped = {};
        this.state.menu.forEach((i) => {
            const c = CATEGORY_ORDER.indexOf(i.category) >= 0 ? i.category : 'other';
            if (!grouped[c]) grouped[c] = [];
            grouped[c].push(i);
        });
        // Cuántos ítems tiene cada rubro: va en el botón, así el cajero sabe si un rubro está
        // vacío ANTES de tocarlo (si no, cambiar y encontrar la nada parece que algo falló).
        const groupCount = (g) => g.cats.reduce((n, c) => n + (grouped[c] ? grouped[c].length : 0), 0);
        const activeGroup = CATEGORY_GROUPS.find((g) => g.key === this.state.group) || CATEGORY_GROUPS[0];
        const visibleCats = CATEGORY_ORDER.filter((c) => activeGroup.cats.indexOf(c) >= 0 && grouped[c]);
        const cartLines = Object.keys(this.state.cart).map((k) => this.state.cart[k]);
        // Eventos con consumo incluido: el resumen del evento de la mesa elegida (null en una
        // mesa común o en Mostrador) y el motivo por el que "Incluido" no se puede elegir.
        const eventSummary = includedOf(this.selectedSeated());
        const eventCounter = formatCounter(eventSummary);
        const includedBlocked = this.includedBlockedReason();
        // En "Incluido", el pedido repartido: qué sale sin cargo y qué va a la cuenta de la mesa.
        const split = this.cartSplit();
        const extraTotal = split ? cartLines.reduce((sum, l) => sum + this.linePrice(l) * split.byCode[l.code].extra, 0) : 0;
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={27} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Cafetería" subtitle="POS" />
                            <div className="row">
                                <div className="col-md-8">
                                    {/* Esto es una pantalla de MOSTRADOR: se usa con gente esperando, así que
                                        lo que importa es ver el menú entero de un vistazo, no que se vea lindo.
                                        Dos decisiones de densidad:
                                          · Las categorías van en COLUMNAS (column-count), no una debajo de otra:
                                            apiladas, "Otros" quedaba dos scrolls abajo. Con multicolumna el alto
                                            total se divide por la cantidad de columnas.
                                          · La card es HORIZONTAL (ícono | nombre sobre precio). La vertical
                                            gastaba ~120px de alto para tres líneas de contenido.
                                        Se usa CSS y no estilos inline para no recalcular un objeto de estilos por
                                        ítem en cada render; sólo queda inline lo que depende del color de la
                                        categoría. */}
                                    <style>{`
                                        .cafe-pos-groups { display: flex; gap: .5rem; }
                                        .cafe-pos-group {
                                            flex: 1 1 0; display: flex; align-items: center; justify-content: center; gap: .4rem;
                                            padding: .55rem .5rem; border: 1px solid #e1e5eb; border-radius: 10px;
                                            background: #fff; color: #5a6169; font-weight: 600; font-size: .9rem;
                                            cursor: pointer; transition: background .12s ease, color .12s ease, border-color .12s ease;
                                        }
                                        .cafe-pos-group:hover { border-color: #c4c8d0; }
                                        .cafe-pos-group.is-active { background: #007bff; border-color: #007bff; color: #fff; }
                                        .cafe-pos-group i { font-size: 19px; }
                                        .cafe-pos-group-n { font-size: .72rem; font-weight: 700; opacity: .65; }
                                        /* Grid y NO column-count: multicolumna llena la primera columna hacia
                                           abajo antes de pasar a la siguiente, así que con 2 bloques quedaban
                                           uno debajo del otro. Además se cortaba por media query (viewport) y lo
                                           que importa es el ancho de ESTE panel, que es 8/12 de la pantalla.
                                           auto-fit + minmax reparte por el ancho real del contenedor: 2 columnas
                                           desde ~660px, 3 desde ~1000px, sin una sola media query. */
                                        .cafe-pos-cats { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: .75rem; align-items: start; }
                                        /* align-items: start arriba + este margin en 0: si no, las cards se
                                           estiran al alto de la más alta de su fila y sobra aire abajo. */
                                        .cafe-pos-cat { margin-bottom: 0 !important; }
                                        .cafe-pos-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(158px, 1fr)); gap: .4rem; }
                                        .cafe-pos-card {
                                            position: relative; display: flex; align-items: center; gap: .45rem;
                                            min-width: 0; padding: .35rem .45rem;
                                            border: 1px solid #e1e5eb; border-radius: 10px; background: #fff;
                                            box-shadow: 0 1px 2px rgba(90,97,105,.1); cursor: pointer;
                                            transition: transform .12s ease, box-shadow .12s ease;
                                        }
                                        .cafe-pos-card:hover { transform: translateY(-2px); box-shadow: 0 6px 14px rgba(90,97,105,.2); }
                                        .cafe-pos-card:active { transform: translateY(0); }
                                        .cafe-pos-card.is-off { opacity: .45; cursor: not-allowed; }
                                        .cafe-pos-card.is-off:hover { transform: none; box-shadow: 0 1px 2px rgba(90,97,105,.1); }
                                        .cafe-pos-ic { flex: 0 0 auto; width: 32px; height: 32px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; }
                                        .cafe-pos-ic i { font-size: 19px; }
                                        /* min-width:0 para que el nombre largo recorte en vez de empujar el precio. */
                                        .cafe-pos-txt { display: flex; flex-direction: column; flex: 1 1 auto; min-width: 0; }
                                        .cafe-pos-name {
                                            font-weight: 600; color: #3d5170; font-size: .8rem; line-height: 1.15;
                                            display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
                                        }
                                        .cafe-pos-price { font-weight: 700; font-size: .8rem; line-height: 1.35; }
                                        .cafe-pos-qty {
                                            position: absolute; top: -7px; right: -7px; color: #fff; border-radius: 999px;
                                            min-width: 20px; height: 20px; padding: 0 5px; font-size: .72rem; font-weight: 700;
                                            display: inline-flex; align-items: center; justify-content: center;
                                            box-shadow: 0 1px 3px rgba(0,0,0,.25);
                                        }
                                        /* El aviso va al lado del precio y no flotando sobre la card: en una card
                                           de este alto, un badge absoluto tapaba el ícono o el nombre. */
                                        .cafe-pos-flag { margin-left: .3rem; border-radius: 5px; padding: 0 4px; font-size: .62rem; font-weight: 700; color: #fff; white-space: nowrap; }
                                    `}</style>
                                    {this.state.loading && (
                                        <div className="card card-small mb-4"><div className="card-body p-4">Cargando menú…</div></div>
                                    )}
                                    {!this.state.loading && this.state.menu.length === 0 && (
                                        <div className="card card-small mb-4"><div className="card-body p-4 text-muted">No hay ítems de menú. Cargá el menú primero (Cafetería → Menú).</div></div>
                                    )}
                                    {/* Primer nivel: rubro. Botones grandes y siempre visibles arriba —
                                        es el control que más se toca y se usa con una mano ocupada. */}
                                    {!this.state.loading && this.state.menu.length > 0 && (
                                        <div className="cafe-pos-groups mb-2">
                                            {CATEGORY_GROUPS.map((g) => {
                                                const active = g.key === activeGroup.key;
                                                const n = groupCount(g);
                                                return (
                                                    <button
                                                        key={g.key}
                                                        type="button"
                                                        className={`cafe-pos-group${active ? ' is-active' : ''}`}
                                                        onClick={() => this.selectGroup(g.key)}
                                                    >
                                                        <i className="material-icons">{g.icon}</i>
                                                        <span>{g.label}</span>
                                                        <span className="cafe-pos-group-n">{n}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {!this.state.loading && this.state.menu.length > 0 && visibleCats.length === 0 && (
                                        <div className="card card-small mb-3"><div className="card-body p-3 text-muted">No hay ítems en {activeGroup.label}.</div></div>
                                    )}
                                    <div className="cafe-pos-cats">
                                    {visibleCats.map((c) => {
                                        const cs = CATEGORY_STYLE[c] || CATEGORY_STYLE.other;
                                        return (
                                        <div className="card card-small mb-2 cafe-pos-cat" key={c}>
                                            <div className="card-header border-bottom py-2 px-3">
                                                <h6 className="m-0 d-flex align-items-center" style={{ fontSize: '.85rem' }}>
                                                    <i className="material-icons mr-2" style={{ fontSize: '18px', color: cs.color }}>{cs.icon}</i>
                                                    {CATEGORY_LABELS[c]}
                                                    <span className="ml-auto text-muted" style={{ fontSize: '.72rem', fontWeight: 400 }}>{grouped[c].length}</span>
                                                </h6>
                                            </div>
                                            <div className="card-body p-2">
                                                <div className="cafe-pos-grid">
                                                    {grouped[c].map((item) => {
                                                        const line = this.state.cart[item.code];
                                                        // ENI-90 (P-06): un ítem no vendible (falta receta) o sin stock (available<=0)
                                                        // se muestra DESHABILITADO con el motivo. is_recipe_based=0 => available null => nunca.
                                                        const unavailable = item.sellable === false || (item.available != null && item.available <= 0);
                                                        const reason = item.sellable === false ? 'Falta receta' : 'Sin stock';
                                                        const low = !unavailable && item.available != null && item.available > 0 && item.available <= 5;
                                                        return (
                                                        <div
                                                            key={item.code}
                                                            role="button"
                                                            title={item.name}
                                                            className={`cafe-pos-card${unavailable ? ' is-off' : ''}`}
                                                            onClick={unavailable ? undefined : () => this.addToCart(item)}
                                                            style={line ? {
                                                                borderColor: cs.color,
                                                                background: `${cs.color}12`,
                                                                boxShadow: `0 0 0 2px ${cs.color}40`,
                                                            } : undefined}
                                                        >
                                                            <span className="cafe-pos-ic" style={{ background: `${cs.color}1a`, color: cs.color }}>
                                                                <i className="material-icons">{cs.icon}</i>
                                                            </span>
                                                            <span className="cafe-pos-txt">
                                                                <span className="cafe-pos-name">{item.name}</span>
                                                                <span className="cafe-pos-price" style={{ color: cs.color }}>
                                                                    {money(item.price)}
                                                                    {unavailable && (<span className="cafe-pos-flag" style={{ background: '#eb5757' }}>{reason}</span>)}
                                                                    {/* ENI-90 (UX): aviso de stock bajo (0 < disponible <= 5). */}
                                                                    {low && (<span className="cafe-pos-flag" style={{ background: '#f2994a' }}>quedan {item.available}</span>)}
                                                                </span>
                                                            </span>
                                                            {line && (
                                                                <span className="cafe-pos-qty" style={{ background: cs.color }}>{line.quantity}</span>
                                                            )}
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="card card-small mb-4">
                                        <div className="card-header border-bottom"><h6 className="m-0">Pedido</h6></div>
                                        <div className="card-body p-3">
                                            {cartLines.length === 0 && (<p className="text-muted">Tocá un ítem para agregarlo.</p>)}
                                            {cartLines.map((l) => (
                                                <div key={l.code} className="mb-2 pb-2 border-bottom">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span>{l.name}</span>
                                                        <span>
                                                            <button type="button" className="btn btn-sm btn-white" onClick={() => this.changeQty(l.code, -1)}>−</button>
                                                            <span className="px-2">{l.quantity}</span>
                                                            <button type="button" className="btn btn-sm btn-white" onClick={() => this.changeQty(l.code, 1)}>+</button>
                                                        </span>
                                                    </div>
                                                    {/* ENI-111: precio efectivo por línea (reprecia con el modo empleado). */}
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small className="text-muted">
                                                            {money(this.linePrice(l))} c/u
                                                            {this.state.employeeSale && l.employeePrice != null && <span className="text-success"> · empleado</span>}
                                                            {this.state.employeeSale && l.employeePrice == null && <span className="text-warning"> · sin precio empleado</span>}
                                                        </small>
                                                        <small className="font-weight-bold">{money(this.linePrice(l) * l.quantity)}</small>
                                                    </div>
                                                    {/* Mesa de un evento, Cobro en "Incluido": cuánto de esta línea sale por
                                                        la entrada y cuánto se cobra, antes de confirmar. */}
                                                    {split && (() => {
                                                        const { included, extra } = split.byCode[l.code];
                                                        const cobra = money(this.linePrice(l) * extra);
                                                        return (
                                                            <small className={`d-block pos-line-split ${extra > 0 ? 'text-warning' : 'text-success'}`}>
                                                                {extra === 0 ? 'incluido' : (included === 0
                                                                    ? `extra ${cobra}`
                                                                    : `${included} ${included === 1 ? 'incluida' : 'incluidas'} · ${extra} extra ${cobra}`)}
                                                            </small>
                                                        );
                                                    })()}
                                                    <input type="text" className="form-control form-control-sm mt-1" placeholder="Nota (ej. sin azúcar)" value={l.notes} onChange={(e) => this.changeNote(l.code, e.target.value)} />
                                                </div>
                                            ))}

                                            {/* ENI-111: modo venta empleado (precio de empleado + cédula obligatoria). */}
                                            <div className="form-group mt-2 mb-1">
                                                <label style={{ cursor: 'pointer', marginBottom: 0 }}>
                                                    <input type="checkbox" checked={this.state.employeeSale} onChange={(e) => this.setEmployeeSale(e.target.checked)} />
                                                    {' '}<strong>Venta empleado</strong>
                                                    <span className="text-muted"> — precio de empleado (requiere cédula)</span>
                                                </label>
                                            </div>

                                            {/* UN solo campo para el cliente: cédula o nombre. Con la cédula, el POS
                                                resuelve la ficha y el saldo a favor de una — pedir la cédula otra vez
                                                más abajo para poder usar el saldo sería pedir dos veces lo mismo. Sin
                                                cédula sigue alcanzando el nombre para identificar el pedido en el
                                                mostrador ("el pedido de Juan"). */}
                                            <div className="form-group mt-2">
                                                <label className="d-block">Cliente {this.state.employeeSale
                                                    ? <span className="text-danger">(cédula del empleado — requerida)</span>
                                                    : <span className="text-muted">(opcional)</span>}</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder={this.state.employeeSale ? 'Cédula del empleado' : 'Cédula o nombre (vacío = Consumidor Final)'}
                                                    value={this.state.customerInput}
                                                    onChange={(e) => this.onChangeCustomer(e.target.value)}
                                                    // Al salir del campo se resuelve el cliente Y, si es una venta de
                                                    // empleado, el saldo de esa cédula entre los ya cargados.
                                                    onBlur={() => { this.lookupCustomer(); this.matchEmployeeAccount(); }}
                                                />
                                                {this.customerIsCedula() && this.state.customerFound && (
                                                    <small className="text-success d-block mt-1">
                                                        {`${this.state.customerFirstName} ${this.state.customerLastName}`.trim() || 'Cliente existente'}
                                                        {this.state.creditBalance > 0 && (
                                                            <span> · saldo a favor {money(this.state.creditBalance)}</span>
                                                        )}
                                                    </small>
                                                )}
                                                {/* FR-091: el saldo, sólo para MIRAR. Cobrar es un acto de caja y se
                                                    hace desde la pantalla de Caja, no acá: en el POS hay alguien
                                                    esperando su pedido (D-06). */}
                                                {this.state.employeeSale && this.state.employeeAccount && (
                                                    <small
                                                        className={`d-block mt-1 ${this.state.employeeAccount.warn ? 'text-danger font-weight-bold' : 'text-muted'}`}
                                                    >
                                                        {this.state.employeeAccount.balance > 0
                                                            ? `Debe ${money(this.state.employeeAccount.balance)}`
                                                            : (this.state.employeeAccount.balance < 0
                                                                ? `Tiene ${money(-this.state.employeeAccount.balance)} a favor`
                                                                : 'Está al día')}
                                                        {this.state.employeeAccount.warn
                                                            ? ` · pasó el tope de ${money(this.state.employeeAccount.threshold)}`
                                                            : ''}
                                                    </small>
                                                )}
                                                {/* Cédula sin ficha: se pide el nombre para darlo de alta al cobrar
                                                    (en venta empleado el backend rechaza: no se auto-crean empleados). */}
                                                {this.customerIsCedula() && !this.state.customerFound && !this.state.employeeSale && (
                                                    <div className="row mt-1">
                                                        <div className="col-12"><small className="text-muted">Cédula sin ficha: se crea el cliente al cobrar.</small></div>
                                                        <div className="col-6 pr-1">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="Nombre"
                                                                value={this.state.customerFirstName}
                                                                onChange={(e) => this.setState({ customerFirstName: e.target.value })}
                                                            />
                                                        </div>
                                                        <div className="col-6 pl-1">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="Apellido"
                                                                value={this.state.customerLastName}
                                                                onChange={(e) => this.setState({ customerLastName: e.target.value })}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group mt-2">
                                                <label className="d-block">Destino</label>
                                                {/* Un solo combo: mostrador o la mesa concreta (de las que tienen gente
                                                    sentada ahora). Elegir la mesa acá es lo que ata el consumo a su cuenta. */}
                                                <select className="form-control form-control-sm" value={this.state.selectedReservationId}
                                                    // Al abrir el combo se vuelven a pedir las mesas: entre un pedido y
                                                    // otro puede haberse sentado o liberado alguna.
                                                    onFocus={this.fetchSeatedTables}
                                                    // Volver al mostrador no puede quedar "cargado a la mesa".
                                                    onChange={(e) => this.onChangeDestination(e.target.value)}>
                                                    <option value="">Mostrador</option>
                                                    {(this.state.seatedTables || []).map((t) => (
                                                        <option key={t.reservationId} value={t.reservationId}>
                                                            {t.label}{t.name ? ` — ${t.name}` : ''} ({t.playerCount}p)
                                                        </option>
                                                    ))}
                                                </select>
                                                {(this.state.seatedTables || []).length === 0 && (
                                                    <small className="text-muted d-block mt-1">
                                                        No hay mesas con gente sentada. Sentalas en <strong>Mesas</strong> para poder mandarles el pedido.
                                                    </small>
                                                )}
                                                {/* Mesa de un evento que declara qué incluye: lo entregado contra lo
                                                    pactado, a la vista antes de cargar nada (FR-061, FR-062). */}
                                                {eventSummary && (
                                                    <div className="mt-1 pos-event-included">
                                                        <small className="d-block">
                                                            Incluido en el evento —{' '}
                                                            {eventCounter.map((row, i) => (
                                                                <span key={i}>
                                                                    {i > 0 ? ' · ' : ''}
                                                                    <span className={row.over ? 'text-danger font-weight-bold' : undefined}>
                                                                        {row.text}{row.stale ? ' (a revisar)' : ''}
                                                                    </span>
                                                                </span>
                                                            ))}
                                                            {eventCounter.length === 0 && 'sin cantidades pactadas'}
                                                        </small>
                                                        {(eventSummary.outside || []).length > 0 && (
                                                            <small className="d-block text-danger">
                                                                Fuera de lo pactado: {eventSummary.outside.map((o) => `${o.quantity} × ${o.name || `Ítem #${o.code}`}`).join(' · ')}
                                                            </small>
                                                        )}
                                                        {Number(eventSummary.unpaidAdmissions) > 0 && (
                                                            <div className="alert alert-warning py-2 mt-1 mb-0">
                                                                La entrada del evento está impaga: {money(eventSummary.unpaidAmount)} a cobrar en Eventos → Anotados
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                {/* Cobro: separado del destino. El destino dice A DÓNDE va el pedido;
                                                    acá se decide CUÁNDO se paga — ahora, o cargado a la cuenta de la
                                                    mesa para cobrarlo después (entero o por partes). */}
                                                <label className="d-block">Cobro</label>
                                                <select className="form-control form-control-sm mb-1" value={this.state.chargeMode}
                                                    onChange={(e) => this.setChargeMode(e.target.value)}>
                                                    <option value="now">Cobrar ahora</option>
                                                    <option value="table" disabled={!this.selectedSeated()}>
                                                        Cargar a la mesa (se cobra después)
                                                    </option>
                                                    <option value="included" disabled={!!includedBlocked}>
                                                        Incluido en el evento (sin cargo)
                                                    </option>
                                                    {/* Sólo en venta de empleado: fuera de ese modo el backend la
                                                        rechaza (3600), así que ofrecerla sería ofrecer un error. */}
                                                    <option value="account" disabled={!this.state.employeeSale}>
                                                        Cargar a la cuenta del empleado
                                                    </option>
                                                    {/* Premio o cortesía de producto: no se combina con la venta de empleado. */}
                                                    <option value="voucher" disabled={this.state.employeeSale}>
                                                        Premio o cortesía (voucher)
                                                    </option>
                                                </select>
                                                {this.state.chargeMode === 'voucher' && (
                                                    <div className="mb-1">
                                                        <div className="input-group input-group-sm">
                                                            <input type="text" className="form-control form-control-sm" placeholder="Código del voucher"
                                                                aria-label="Código del voucher" value={this.state.voucherCode}
                                                                onChange={(e) => this.setState({ voucherCode: e.target.value, voucherPreview: null })} />
                                                            <div className="input-group-append">
                                                                <button type="button" className="btn btn-white" disabled={this.state.voucherLoading} onClick={this.previewVoucher}>
                                                                    {this.state.voucherLoading ? '…' : 'Validar'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {this.state.voucherPreview && (
                                                            <small className={`d-block mt-1 ${this.state.voucherPreview.fits ? 'text-success' : 'text-danger'}`}>
                                                                {this.state.voucherPreview.label} — {this.state.voucherPreview.benefit}.{' '}
                                                                {this.state.voucherPreview.fits ? 'El pedido entra: sale sin cargo.' : (this.state.voucherPreview.reason || 'Agregá al pedido lo que cubre.')}
                                                            </small>
                                                        )}
                                                        {!this.state.voucherPreview && (
                                                            <small className="text-muted d-block mt-1">
                                                                Lo que el voucher no cubre se cobra en otro pedido.
                                                            </small>
                                                        )}
                                                    </div>
                                                )}
                                                {this.state.chargeMode === 'table' && this.selectedSeated() && (
                                                    <small className="text-muted d-block mb-1">
                                                        Queda impago en la cuenta de {this.selectedSeated().label}.
                                                    </small>
                                                )}
                                                {this.state.chargeMode === 'included' && (
                                                    <small className="text-muted d-block mb-1">
                                                        Lo incluido sale sin cargo; lo demás va a la cuenta de la mesa.
                                                    </small>
                                                )}
                                                {/* Con una mesa elegida, por qué no se le puede cargar como incluido. */}
                                                {this.selectedSeated() && includedBlocked && (
                                                    <small className="text-muted d-block mb-1 pos-included-blocked">
                                                        «Incluido en el evento» no disponible. {includedBlocked}.
                                                    </small>
                                                )}
                                                {this.state.chargeMode === 'account' && (
                                                    <small className="text-muted d-block mb-1">
                                                        Se suma a la deuda del empleado. Se salda desde <strong>Caja</strong>,
                                                        con el comprobante de la transferencia.
                                                    </small>
                                                )}
                                                {this.state.chargeMode === 'now' && (
                                                <>
                                                {/* El saldo a favor es un medio de pago más: sale del cliente ya
                                                    identificado arriba y sólo aparece si tiene saldo. */}
                                                <select className="form-control form-control-sm"
                                                    value={this.state.payWithCredit ? 'credit' : this.state.paymentType}
                                                    onChange={(e) => this.onChangePayment(e.target.value)}>
                                                    {PAYMENT_METHODS.map((p) => (<option key={p.id} value={p.id}>{p.label}</option>))}
                                                    {this.state.creditBalance > 0 && (
                                                        <option value="credit">Saldo a favor ({money(this.state.creditBalance)})</option>
                                                    )}
                                                </select>
                                                {/* Cuando el saldo no cubre todo, el resto se cobra con otro medio: el
                                                    cajero elige cuál acá mismo, sin tener que deshacer el saldo. */}
                                                {this.state.payWithCredit && !this.creditCoversAll() && (
                                                    <div className="mt-2">
                                                        <label className="d-block mb-1" style={{ fontSize: '.8rem' }}>
                                                            El saldo no alcanza — cobrar {money(this.amountToCharge())} con:
                                                        </label>
                                                        <select className="form-control form-control-sm" value={this.state.paymentType}
                                                            onChange={(e) => this.setState({ paymentType: Number(e.target.value) })}>
                                                            {PAYMENT_METHODS.map((p) => (<option key={p.id} value={p.id}>{p.label}</option>))}
                                                        </select>
                                                    </div>
                                                )}
                                                {!this.creditCoversAll() && this.state.paymentType === 5 && this.state.posDevices.length > 1 && (
                                                    <select
                                                        className="form-control form-control-sm mt-2"
                                                        value={this.state.posDeviceId}
                                                        onChange={(e) => this.setState({ posDeviceId: e.target.value })}
                                                    >
                                                        {this.state.posDevices.map((d) => (
                                                            <option key={d.id} value={d.id}>{d.name}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                </>
                                                )}
                                            </div>

                                            {/* ENI-99: código de descuento con preview en vivo. Deshabilitado en venta
                                                empleado (ENI-111, D-04) y al cargar a la mesa: ver discountBlockedReason.
                                                En modo incluido no se dibuja: no hay nada que descontar (FR-125). */}
                                            {this.state.chargeMode !== 'included' && this.state.chargeMode !== 'voucher' && (
                                            <div className="form-group">
                                                <label className="d-block">Código de descuento <span className="text-muted">(opcional)</span></label>
                                                <div className="input-group input-group-sm">
                                                    <input type="text" className="form-control form-control-sm" placeholder={this.discountBlockedReason() || 'Código'}
                                                        value={this.state.discountCode}
                                                        disabled={!!this.discountBlockedReason()}
                                                        onChange={(e) => this.setState({ discountCode: e.target.value, discountApplied: null })} />
                                                    <div className="input-group-append">
                                                        <button type="button" className="btn btn-white" disabled={this.state.discountLoading || !!this.discountBlockedReason()} onClick={this.applyDiscount}>
                                                            {this.state.discountLoading ? '…' : 'Aplicar'}
                                                        </button>
                                                    </div>
                                                </div>
                                                {this.state.chargeMode === 'table' && !this.state.employeeSale && (
                                                    <small className="text-muted d-block mt-1">
                                                        Los códigos se aplican cobrando el pedido ahora. Lo que se carga a la mesa se cobra después, a precio de lista.
                                                    </small>
                                                )}
                                                {this.state.discountApplied && (
                                                    <small className="text-success d-block mt-1">
                                                        Descuento {this.state.discountApplied.code}: −{money(this.state.discountApplied.discountAmount)}{' '}
                                                        <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={this.clearDiscount}>quitar</button>
                                                    </small>
                                                )}
                                            </div>
                                            )}

                                            {/* Con saldo aplicado el resumen se abre en tres líneas: qué vale el
                                                pedido, cuánto se paga con saldo y cuánta plata hay que cobrar. Sin
                                                saldo queda exactamente como antes. */}
                                            {this.appliedCredit() > 0 && (
                                                <React.Fragment>
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <span className="text-muted">Pedido</span>
                                                        <span className="text-muted">{money(this.orderTotal())}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-1 text-success">
                                                        <span>Saldo a favor</span>
                                                        <span>−{money(this.appliedCredit())}</span>
                                                    </div>
                                                </React.Fragment>
                                            )}
                                            <div className="d-flex justify-content-between font-weight-bold mb-2">
                                                <span>{this.appliedCredit() > 0 ? 'A cobrar' : 'Total'}</span>
                                                <span>
                                                    {this.state.chargeMode === 'voucher' ? (
                                                        <React.Fragment>
                                                            <del className="text-muted mr-2" style={{ fontWeight: 'normal' }}>{money(this.total())}</del>
                                                            {money(0)}
                                                        </React.Fragment>
                                                    ) : this.state.chargeMode === 'included' ? (
                                                        // Lo incluido a precio de lista, tachado, y lo que va a la cuenta.
                                                        <React.Fragment>
                                                            <del className="text-muted mr-2" style={{ fontWeight: 'normal' }}>{money(this.total() - extraTotal)}</del>
                                                            A la cuenta de la mesa: {money(extraTotal)}
                                                        </React.Fragment>
                                                    ) : this.state.discountApplied && this.appliedCredit() === 0 ? (
                                                        <React.Fragment>
                                                            <del className="text-muted mr-2" style={{ fontWeight: 'normal' }}>{money(this.state.discountApplied.originalPrice)}</del>
                                                            {money(this.state.discountApplied.totalPrice)}
                                                        </React.Fragment>
                                                    ) : money(this.amountToCharge())}
                                                </span>
                                            </div>
                                            {this.creditCoversAll() && (
                                                <small className="text-muted d-block mb-2" style={{ marginTop: '-6px' }}>
                                                    Lo cubre el saldo. Le quedan {money(Math.max(0, Number(this.state.creditBalance) - this.appliedCredit()))} a favor.
                                                </small>
                                            )}
                                            {/* El texto más largo («Registrar incluido e imprimir comanda») no entra en una
                                                línea en el panel a 1024 px: que parta en dos antes que cortarse. */}
                                            <button type="button" className="btn btn-accent btn-block" style={{ whiteSpace: 'normal' }} disabled={this.state.disabled || cartLines.length === 0} onClick={this.confirm}>
                                                {{
                                                    table: 'Cargar a la mesa e imprimir comanda',
                                                    account: 'Cargar a la cuenta e imprimir comanda',
                                                    voucher: 'Canjear voucher e imprimir comanda',
                                                    // Según cómo se reparte el pedido: todo incluido, con extras, o nada incluido.
                                                    included: (!split || split.extra === 0 || cartLines.length === 0)
                                                        ? 'Registrar incluido e imprimir comanda'
                                                        : (split.included > 0 ? 'Registrar e imprimir comandas' : 'Cargar a la mesa e imprimir comanda'),
                                                }[this.state.chargeMode] || 'Cobrar e imprimir comanda'}
                                            </button>
                                            {this.state.lastKitchenOrderId && (
                                                <button type="button" className="btn btn-white btn-block mt-2" disabled={this.state.reprinting} onClick={this.reprintLast}>
                                                    Reimprimir última (#{this.state.lastKitchenOrderId})
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default CafeteriaPosPage;
